import { NgIf, AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { ChipItem, ChipsFilterComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  BodyConditionType,
  FeedAdviceProperties,
  FeedAdviceType,
  FeedIntakeTypes,
  FeedingType,
  GeneticLineType,
  InputSystemType,
  PhaseTransitionTypes,
} from '../../models/feed-advice.model';
import { SchemeSupportService } from '../../services/scheme-support.service';
import { setLocationsForFeedAdvice } from '../../store/feed-advice.actions';
import { FeedAdvicePropertiesComponent } from '../feed-advice-properties/feed-advice-properties.component';
import { SowsFeedAdvicePropertiesComponent } from '../sows/sows-feed-advice-properties/sows-feed-advice-properties.component';

@Component({
  selector: 'app-feed-advice-settings',
  templateUrl: './feed-advice-settings.component.html',
  styleUrls: ['./feed-advice-settings.component.scss'],
  imports: [
    NgIf,
    ChipsFilterComponent,
    FeedAdvicePropertiesComponent,
    SowsFeedAdvicePropertiesComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FeedAdviceSettingsComponent implements OnChanges {
  @Input() isReferenceFeedAdvice: boolean;
  @Input() properties: FeedAdviceProperties;
  @Input() locations: number[];
  locationChipItems: ChipItem<number>[] = [];
  selectedLocations: number[];
  @Output() settingsChanged: EventEmitter<{
    locations: number[];
    properties: FeedAdviceProperties;
  }> = new EventEmitter<{
    locations: number[];
    properties: FeedAdviceProperties;
  }>();
  locations$: Observable<ChipItem<number>[]> = this.store
    .select(UserSelectors.selectLocations)
    .pipe(
      take(1),
      map((userlocations) => {
        userlocations.forEach((userlocation) => {
          if (userlocation.isSelected) {
            this.store.dispatch(setLocationsForFeedAdvice({ locations: [userlocation.id] }));
          }
          this.locationChipItems.push({
            value: userlocation.id,
            name: userlocation.name,
            selected:
              userlocation.isSelected ||
              (!!this.locations && this.locations.some((l) => l === userlocation.id)),
          });
        });
        return this.locationChipItems;
      }),
    );
  feedintake = FeedIntakeTypes.LOW;
  phases: number;
  terminalBoar = '';
  selectedGenders: Array<string>;
  genders: ChipItem<number>[] = this.schemeSupportService.getSelectedGenders();
  feedAdviceType: FeedAdviceType = FeedAdviceType.FATTENING;
  phaseTransition: PhaseTransitionTypes = PhaseTransitionTypes.GRADUAL;
  bodyCondition: BodyConditionType;
  geneticLine: GeneticLineType;
  inputSystem: InputSystemType = InputSystemType.ENERGYDAY;
  transitionDay: number;
  pigletsPerFarrowingCategory: number;
  weaningWeight: number;
  weaningAge: number;
  rearingAge: number;
  showLysine: boolean;
  showEnergy: boolean;
  showDosator: boolean;
  selectedDosatorSettingsId?: number;
  feedingType?: FeedingType;

  @ViewChild(FeedAdvicePropertiesComponent) propertiesComponent: FeedAdvicePropertiesComponent;

  constructor(
    private readonly store: Store<RootState>,
    private readonly schemeSupportService: SchemeSupportService,
  ) {}

  // Set the properties to the values passed in from the selected feedadvice.
  ngOnChanges(changes: SimpleChanges) {
    if (Object.prototype.hasOwnProperty.call(changes, 'locations')) {
      this.selectedLocations = [...(changes.locations.currentValue ?? [])];
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'properties')) {
      const properties = <FeedAdviceProperties>changes.properties.currentValue;

      this.feedintake = properties.feedintake;
      this.phases = properties.phases;
      this.terminalBoar = properties.terminalBoar;
      this.selectedGenders = [...(properties.genders ?? [])];
      this.genders = this.schemeSupportService.getSelectedGenders(properties.genders);
      this.feedAdviceType = properties.feedAdviceType;
      this.phaseTransition = properties.phaseTransition;
      this.inputSystem = properties.inputSystem;
      this.showLysine = properties.showLysine;
      this.showEnergy = properties.showEnergy;
      this.showDosator = properties.showDosator;

      // SOWS SPECIFIC PROPERTIES
      this.bodyCondition = properties.bodyCondition;
      this.geneticLine = properties.geneticLine ?? null;
      this.transitionDay = properties.transitionDay;
      this.pigletsPerFarrowingCategory = properties.pigletsPerFarrowingCategory;

      // WEANED PIGLETS SPECIFIC PROPERTIES
      this.weaningWeight = properties.weaningWeight;
      this.weaningAge = properties.weaningAge;

      // REARING PIGLETS SPECIFIC PROPERTIES
      this.rearingAge = properties.rearingAge;
      this.selectedDosatorSettingsId = properties.selectedDosatorSettingsId;

      // REARING GILTS SPECIFIC PROPERTIES
      this.feedingType = properties.feedingType;
    }
  }

  isFatteningOrRearingOrWeaningPigletsFeedadvice() {
    return (
      this.feedAdviceType === FeedAdviceType.FATTENING ||
      this.feedAdviceType === FeedAdviceType.REARING ||
      this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS
    );
  }

  isSowsFeedadvice() {
    return (
      this.feedAdviceType === FeedAdviceType.SOWS_BREEDTOFARROW ||
      this.feedAdviceType === FeedAdviceType.SOWS_FARROWTOWEAN ||
      this.feedAdviceType === FeedAdviceType.SOWS_WEANTOBREED
    );
  }

  changeLocation(selectedInput: number | number[]) {
    const emit = this.selectedLocations !== undefined;
    const selected =
      selectedInput === undefined
        ? []
        : Array.isArray(selectedInput)
          ? selectedInput
          : [selectedInput];

    this.selectedLocations = [...selected];

    if (emit) {
      this.emitSettingsChangedEvent();
    }
  }

  changeFeedAdviceProperties(properties: FeedAdviceProperties) {
    this.selectedGenders = properties.genders;
    this.feedintake = properties.feedintake;
    this.terminalBoar = properties.terminalBoar;
    this.phases = properties.phases;
    this.feedAdviceType = properties.feedAdviceType;
    this.phaseTransition = properties.phaseTransition;
    this.geneticLine = properties.geneticLine ?? null;
    this.bodyCondition = properties.bodyCondition;
    this.inputSystem = properties.inputSystem;
    this.transitionDay = properties.transitionDay;
    this.pigletsPerFarrowingCategory = properties.pigletsPerFarrowingCategory;
    this.weaningWeight = properties.weaningWeight;
    this.weaningAge = properties.weaningAge;
    this.rearingAge = properties.rearingAge;
    this.showLysine = properties.showLysine;
    this.showEnergy = properties.showEnergy;
    this.showDosator = properties.showDosator;
    this.selectedDosatorSettingsId = properties.selectedDosatorSettingsId;
    this.feedingType = properties.feedingType;
    this.emitSettingsChangedEvent();
  }

  private emitSettingsChangedEvent() {
    this.settingsChanged.emit({
      locations: [...(this.selectedLocations ?? this.locations ?? [])],
      properties: {
        phaseTransition: this.phaseTransition,
        terminalBoar: this.terminalBoar,
        feedAdviceType: this.feedAdviceType,
        genders: this.selectedGenders,
        feedintake: this.feedintake,
        phases: this.phases,
        bodyCondition: this.bodyCondition,
        geneticLine: this.geneticLine,
        inputSystem: this.inputSystem,
        transitionDay: this.transitionDay,
        pigletsPerFarrowingCategory: this.pigletsPerFarrowingCategory,
        weaningWeight: this.weaningWeight,
        weaningAge: this.weaningAge,
        rearingAge: this.rearingAge,
        showLysine: this.showLysine,
        showEnergy: this.showEnergy,
        showDosator: this.showDosator,
        selectedDosatorSettingsId: this.selectedDosatorSettingsId,
        feedingType: this.feedingType,
      },
    });
  }
}
